import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Printer, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExternalWorkOrders({ user }) {
  const navigate = useNavigate();
  const { hasPermission, canCreate, canEdit, canDelete, canApprove } = usePermissions(user);
  const [workOrders, setWorkOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [itemFilter, setItemFilter] = useState(''); // Filter by item name
  const [fundFilter, setFundFilter] = useState(''); // Filter by fund
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [selectedWoId, setSelectedWoId] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);
  const [statusAuthDialogOpen, setStatusAuthDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [selectedExecuteWoId, setSelectedExecuteWoId] = useState(null);
  const [receiptImages, setReceiptImages] = useState([]);
  const [totalValuePKR, setTotalValuePKR] = useState('');
  const [bypassReceipts, setBypassReceipts] = useState(false);
  const [receiptViewOpen, setReceiptViewOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  
  // Master data
  const [fabrics, setFabrics] = useState([]);
  const [articles, setArticles] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    work_order_type: '',
    department: '',
    item_category: '',
    item_id: '',
    item_name: '',
    color_code: '',
    qty_meters: '',
    description: '',
    fund: '',
    requested_budget: ''
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [availableColors, setAvailableColors] = useState([]);

  // PKR to USD conversion rate
  const PKR_TO_USD_RATE = 280; // 1 USD = 280 PKR

  const convertPKRtoUSD = (pkr) => {
    if (!pkr || parseFloat(pkr) <= 0) return 0;
    return (parseFloat(pkr) / PKR_TO_USD_RATE).toFixed(2);
  };

  const fundOptions = [
    { value: 'petty_cash', label: 'Petty Cash' },
    { value: 'finance_department', label: 'Finance Department' }
  ];

  const itemCategoryOptions = [
    { value: 'fabric', label: 'Fabric LN' },
    { value: 'article', label: 'Article LN-X' }
  ];

  const workOrderTypeOptions = [
    { value: 'inventory_based', label: 'Inventory Based' },
    { value: 'non_inventory_based', label: 'Non-Inventory Based' }
  ];

  useEffect(() => {
    fetchWorkOrders();
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch fabrics
      const fabricsRes = await fetch(`${API}/fabrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (fabricsRes.ok) {
        const fabricsData = await fabricsRes.json();
        setFabrics(fabricsData);
      }

      // Fetch articles
      const articlesRes = await fetch(`${API}/articles-master`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (articlesRes.ok) {
        const articlesData = await articlesRes.json();
        setArticles(articlesData);
      }
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/external-work-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkOrders(data);
      }
    } catch (error) {
      toast.error('Failed to fetch work orders');
    }
  };

  // Auto-refresh every 5 seconds, but pause when any dialog is open
  const isUserEditing = open || viewDialogOpen || statusAuthDialogOpen || executeDialogOpen || authDialogOpen;
  useAutoRefresh(fetchWorkOrders, 5, isUserEditing);

  const handleWorkOrderTypeChange = (value) => {
    setFormData({
      ...formData,
      work_order_type: value,
      item_category: '',
      item_id: '',
      item_name: '',
      color_code: '',
      qty_meters: ''
    });
    setSelectedItem(null);
    setAvailableColors([]);
  };

  const handleItemCategoryChange = (value) => {
    setFormData({
      ...formData,
      item_category: value,
      item_id: '',
      item_name: '',
      color_code: '',
      qty_meters: ''
    });
    setSelectedItem(null);
    setAvailableColors([]);
  };

  const handleItemSelect = (itemId) => {
    const items = formData.item_category === 'fabric' ? fabrics : articles;
    const item = items.find(i => i.id === itemId);
    
    if (item) {
      setSelectedItem(item);
      setFormData({
        ...formData,
        item_id: item.id,
        item_name: formData.item_category === 'fabric' ? item.ln_code : item.ln_x_code
      });

      // If fabric, set available colors
      if (formData.item_category === 'fabric' && item.colors) {
        setAvailableColors(item.colors);
      } else {
        setAvailableColors([]);
        setFormData(prev => ({ ...prev, color_code: '' }));
      }
    }
  };

  const getItemOptions = () => {
    if (!formData.item_category) return [];
    
    if (formData.item_category === 'fabric') {
      return fabrics.map(f => ({
        value: f.id,
        label: f.ln_code || 'N/A'
      }));
    } else {
      return articles.map(a => ({
        value: a.id,
        label: `${a.ln_x_code || 'N/A'} - ${a.article_category || 'N/A'}`
      }));
    }
  };

  const getColorOptions = () => {
    return availableColors.map(c => ({
      value: c.color_code,
      label: `${c.color_code} - ${c.color_name}`
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.work_order_type) {
      toast.error('Please select work order type');
      return;
    }
    
    if (!formData.department) {
      toast.error('Please select department');
      return;
    }
    
    // Inventory-based validations
    if (formData.work_order_type === 'inventory_based') {
      if (!formData.item_category) {
        toast.error('Please select item category');
        return;
      }
      if (!formData.item_id) {
        toast.error('Please select an item');
        return;
      }
      if (formData.item_category === 'fabric') {
        if (!formData.color_code) {
          toast.error('Please select a color code for fabric');
          return;
        }
        if (!formData.qty_meters || parseFloat(formData.qty_meters) <= 0) {
          toast.error('Please enter quantity in meters');
          return;
        }
      }
    }
    
    if (!formData.fund) {
      toast.error('Please select fund');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Prepare data with proper types
      const dataToSubmit = {
        ...formData,
        requested_budget: formData.requested_budget ? parseFloat(formData.requested_budget) : null,
        qty_meters: formData.qty_meters ? parseFloat(formData.qty_meters) : null
      };
      
      console.log('Submitting work order:', dataToSubmit);
      
      if (editingWorkOrder) {
        // Update existing work order
        const response = await fetch(`${API}/external-work-orders/${editingWorkOrder.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(dataToSubmit)
        });

        if (response.ok) {
          toast.success('Work order updated successfully');
          setOpen(false);
          setEditingWorkOrder(null);
          resetForm();
          fetchWorkOrders();
        } else {
          const error = await response.json();
          console.error('Update error:', error);
          toast.error(error.detail || 'Failed to update work order');
        }
      } else {
        // Create new work order
        const response = await fetch(`${API}/external-work-orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(dataToSubmit)
        });

        if (response.ok) {
          toast.success('Work order created successfully');
          setOpen(false);
          resetForm();
          fetchWorkOrders();
        } else {
          const error = await response.json();
          console.error('Create error:', error);
          toast.error(error.detail || 'Failed to create work order');
        }
      }
    } catch (error) {
      console.error('Error saving work order:', error);
      toast.error(editingWorkOrder ? 'Failed to update work order' : 'Failed to create work order');
    }
  };

  const resetForm = () => {
    setFormData({
      work_order_type: '',
      department: '',
      item_category: '',
      item_id: '',
      item_name: '',
      color_code: '',
      qty_meters: '',
      description: '',
      fund: '',
      requested_budget: ''
    });
    setSelectedItem(null);
    setAvailableColors([]);
  };

  const handleViewClick = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setViewDialogOpen(true);
  };

  const handleStatusChangeRequest = (woId, newStatus) => {
    setPendingStatusChange({ woId, newStatus });
    setStatusAuthDialogOpen(true);
  };

  const handleStatusChangeConfirm = async () => {
    if (!pendingStatusChange) return;

    const { woId, newStatus } = pendingStatusChange;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/external-work-orders/${woId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Status updated to ${newStatus}`);
        setViewDialogOpen(false);
        setStatusAuthDialogOpen(false);
        setPendingStatusChange(null);
        fetchWorkOrders();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleExecuteClick = (woId) => {
    setSelectedExecuteWoId(woId);
    setReceiptImages([]);
    setTotalValuePKR('');
    setBypassReceipts(false);
    setExecuteDialogOpen(true);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const imagePromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(imagePromises).then(images => {
      setReceiptImages(prev => [...prev, ...images]);
    });
  };

  const handleRemoveImage = (index) => {
    setReceiptImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleExecuteSubmit = async () => {
    if (!bypassReceipts) {
      if (receiptImages.length === 0) {
        toast.error('Please upload at least one receipt or use bypass');
        return;
      }
      if (!totalValuePKR || parseFloat(totalValuePKR) <= 0) {
        toast.error('Please enter a valid total value in PKR or use bypass');
        return;
      }
    }

    const totalUSD = totalValuePKR ? convertPKRtoUSD(totalValuePKR) : 0;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/external-work-orders/${selectedExecuteWoId}/execute`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          receipts: bypassReceipts ? [] : receiptImages,
          total_value_pkr: bypassReceipts ? 0 : parseFloat(totalValuePKR),
          total_value_usd: bypassReceipts ? 0 : parseFloat(totalUSD)
        })
      });

      if (response.ok) {
        toast.success('Work order executed successfully');
        setExecuteDialogOpen(false);
        setSelectedExecuteWoId(null);
        setReceiptImages([]);
        setTotalValuePKR('');
        setBypassReceipts(false);
        fetchWorkOrders();
      } else {
        toast.error('Failed to execute work order');
      }
    } catch (error) {
      toast.error('Failed to execute work order');
    }
  };

  const handleReceiptClick = (receipt) => {
    setSelectedReceipt(receipt);
    setReceiptViewOpen(true);
  };

  const handlePrintReceipt = () => {
    if (!selectedReceipt) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Receipt</title>
          <style>
            body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
            img { max-width: 100%; height: auto; }
            @media print {
              body { margin: 0; }
              img { max-width: 100%; page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <img src="${selectedReceipt}" onload="window.print();" />
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrintWorkOrder = (workOrder) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Work Order - ${workOrder?.wo_code}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { border: 2px solid #000; padding: 15px; margin-bottom: 20px; }
            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin: 20px 0; }
            .field { margin-bottom: 10px; }
            .label { font-weight: bold; font-size: 12px; color: #666; }
            .value { font-size: 14px; color: #000; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; }
            th { background: #f0f0f0; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="grid">
              <div>
                <div class="label">WO NUMBER</div>
                <div class="value" style="font-size: 20px; font-weight: bold;">${workOrder?.wo_code || ''}</div>
              </div>
              <div style="text-align: right;">
                ${workOrder?.work_order_type === 'inventory_based' && workOrder?.barcode && workOrder?.barcode.image ? 
                  `<img src="${workOrder.barcode.image}" alt="Barcode" style="height: 60px;"/>
                  <div style="font-size: 10px;">${workOrder.wo_code}</div>` : 
                  '<div class="label">TYPE</div><div class="value">NON-INVENTORY</div>'}
              </div>
            </div>
            <div class="grid" style="margin-top: 15px;">
              <div>
                <div class="label">${workOrder?.item_category === 'fabric' ? 'FABRIC CODE' : workOrder?.item_category === 'article' ? 'ARTICLE CODE' : 'ITEM'}</div>
                <div class="value">${workOrder?.item_name || 'N/A'}</div>
              </div>
              <div>
                <div class="label">STATUS</div>
                <div class="value">${workOrder?.status?.toUpperCase() || ''}</div>
              </div>
            </div>
            <div class="grid">
              <div>
                <div class="label">CREATED</div>
                <div class="value">${new Date(workOrder?.created_at).toLocaleString() || ''}</div>
              </div>
              <div>
                <div class="label">CREATED BY</div>
                <div class="value">${workOrder?.created_by || 'N/A'}</div>
              </div>
            </div>
          </div>
          <div style="border: 1px solid #000; padding: 15px; margin-bottom: 20px;">
            <div class="label">DESCRIPTION</div>
            <div class="value">${workOrder?.description || ''}</div>
          </div>
          ${workOrder?.work_order_type === 'inventory_based' && workOrder?.item_category === 'fabric' ? `
            <div style="border: 1px solid #000; padding: 15px;">
              <div class="label" style="margin-bottom: 10px;">FABRIC REQUIRED FOR INVENTORY ISSUANCE</div>
              <table>
                <thead>
                  <tr>
                    <th>FABRIC CODE</th>
                    <th>COLOR CODE</th>
                    <th>QUANTITY</th>
                    <th>FUND</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>${workOrder?.item_name || 'N/A'}</td>
                    <td>${workOrder?.color_code || 'N/A'}</td>
                    <td>${workOrder?.qty_meters || 'N/A'} meters</td>
                    <td>${workOrder?.fund === 'petty_cash' ? 'Petty Cash' : 'Finance Department'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ` : ''}
          ${workOrder?.requested_budget ? `
            <div style="border: 1px solid #000; padding: 15px; margin-bottom: 20px; background-color: #f9f9f9;">
              <div style="color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px;">Requested Budget</div>
              <div style="font-size: 20px; font-weight: bold;">PKR ${parseFloat(workOrder.requested_budget).toLocaleString()}</div>
            </div>
          ` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  const handleEditClick = (workOrder) => {
    setEditingWorkOrder(workOrder);
    setFormData({
      work_order_type: workOrder.work_order_type || '',
      department: workOrder.department || '',
      item_category: workOrder.item_category || '',
      item_id: workOrder.item_id || '',
      item_name: workOrder.item_name || '',
      color_code: workOrder.color_code || '',
      qty_meters: workOrder.qty_meters || '',
      description: workOrder.description || '',
      fund: workOrder.fund || '',
      requested_budget: workOrder.requested_budget || ''
    });
    
    // Set selected item for editing
    if (workOrder.item_id) {
      const items = workOrder.item_category === 'fabric' ? fabrics : articles;
      const item = items.find(i => i.id === workOrder.item_id);
      if (item) {
        setSelectedItem(item);
        if (workOrder.item_category === 'fabric' && item.colors) {
          setAvailableColors(item.colors);
        }
      }
    }
    
    setOpen(true);
  };

  const handleDeleteClick = (woId) => {
    setSelectedWoId(woId);
    setAuthDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedWoId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/external-work-orders/${selectedWoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Work order deleted successfully');
        setAuthDialogOpen(false);
        setSelectedWoId(null);
        fetchWorkOrders();
      } else {
        toast.error('Failed to delete work order');
      }
    } catch (error) {
      toast.error('Failed to delete work order');
    }
  };

  const getWorkOrdersByStatus = (status) => {
    let filtered;
    
    // Map tab status to actual statuses
    if (status === 'approved') {
      filtered = workOrders.filter(wo => wo.status === 'approved' || wo.status === 'authorized');
    } else if (status === 'rejected') {
      filtered = workOrders.filter(wo => wo.status === 'rejected' || wo.status === 'denied');
    } else {
      filtered = workOrders.filter(wo => wo.status === status);
    }
    
    // Apply search filter
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(wo => 
        wo.wo_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (wo.item_name && wo.item_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (wo.department && wo.department.toLowerCase().includes(searchTerm.toLowerCase())) ||
        wo.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply department filter
    if (itemFilter) {
      filtered = filtered.filter(wo => wo.department === itemFilter);
    }
    
    // Apply fund filter
    if (fundFilter) {
      filtered = filtered.filter(wo => wo.fund === fundFilter);
    }
    
    return filtered;
  };
  
  // Get unique departments for filter dropdown
  const getUniqueItems = () => {
    const departments = [...new Set(workOrders.map(wo => wo.department).filter(Boolean))];
    return departments.sort().map(dept => ({ value: dept, label: dept }));
  };
  
  // Get unique departments for form dropdown
  const getUniqueDepartments = () => {
    const departments = [...new Set(workOrders.map(wo => wo.department).filter(Boolean))];
    return departments.sort().map(dept => ({ value: dept, label: dept }));
  };
  
  // Get unique funds for filter dropdown
  const getUniqueFunds = () => {
    const funds = [...new Set(workOrders.map(wo => wo.fund).filter(Boolean))];
    return funds.map(fund => ({
      value: fund,
      label: fund === 'petty_cash' ? 'Petty Cash' : 'Finance Department'
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const getDisplayDate = (wo) => {
    if (wo.status === 'approved' || wo.status === 'authorized') {
      return wo.date_of_approval || wo.created_at;
    } else if (wo.status === 'rejected' || wo.status === 'denied') {
      return wo.date_of_rejection || wo.created_at;
    } else if (wo.status === 'executed') {
      return wo.date_of_execution || wo.created_at;
    }
    return wo.created_at;
  };

  const calculateExecutedTotals = () => {
    // Use getWorkOrdersByStatus to apply all filters (search, item, fund)
    const executedWOs = getWorkOrdersByStatus('executed');
    const totalPKR = executedWOs.reduce((sum, wo) => sum + (wo.total_value_pkr || 0), 0);
    const totalUSD = executedWOs.reduce((sum, wo) => sum + (wo.total_value_usd || 0), 0);
    return { totalPKR, totalUSD, count: executedWOs.length };
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      authorized: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800',
      executed: 'bg-blue-100 text-blue-800'
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusOptionsForFund = (fund) => {
    if (fund === 'petty_cash') {
      return [
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' }
      ];
    } else if (fund === 'finance_department') {
      return [
        { value: 'authorized', label: 'Authorized' },
        { value: 'denied', label: 'Denied' }
      ];
    }
    // Default to Petty Cash options for old work orders with no fund
    return [
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' }
    ];
  };

  const getAuthCodeForFund = (fund) => {
    if (fund === 'petty_cash') {
      return 'extwo';
    } else if (fund === 'finance_department') {
      return '1436';
    }
    // Default to extwo for old work orders with no fund
    return 'extwo';
  };

  const renderWorkOrdersTable = (status) => {
    const orders = getWorkOrdersByStatus(status);
    const executedTotals = status === 'executed' ? calculateExecutedTotals() : null;

    return (
      <div className="space-y-4">
        {/* Summary Card for Executed Status */}
        {status === 'executed' && executedTotals && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Executed Summary:</span>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Orders</p>
                  <p className="text-lg font-bold text-gray-900">{executedTotals.count}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Total PKR</p>
                  <p className="text-lg font-bold text-gray-900">
                    ₨ {executedTotals.totalPKR.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Total USD</p>
                  <p className="text-lg font-bold text-gray-900">
                    $ {executedTotals.totalUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  WO NUMBER
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  DEPARTMENT
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ITEM
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  FUND
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {status === 'pending' ? 'DATE OF CREATION' :
                   status === 'approved' ? 'DATE OF APPROVAL' :
                   status === 'rejected' ? 'DATE OF REJECTION' :
                   status === 'executed' ? 'DATE OF EXECUTION' : 'DATE'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  STATUS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((wo) => (
                <tr key={wo.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleViewClick(wo)}
                      className="text-sm font-medium text-black hover:underline cursor-pointer"
                    >
                      {wo.wo_code}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {wo.department || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{wo.item_name || 'N/A'}</div>
                      {wo.color_code && (
                        <div className="text-xs text-gray-500">Color: {wo.color_code}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {fundOptions.find(f => f.value === wo.fund)?.label || wo.fund || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">{formatDate(getDisplayDate(wo))}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${getStatusBadgeClass(wo.status)}`}>
                      {wo.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2 items-center">
                      {/* Pending status: Edit and Delete */}
                      {wo.status === 'pending' && (
                        <>
                          {canEdit('external_work_orders') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditClick(wo)}
                              className="border-gray-300 text-gray-600 hover:bg-gray-50"
                            >
                              Edit
                            </Button>
                          )}
                          {canDelete('external_work_orders') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(wo.id)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          )}
                        </>
                      )}
                      
                      {/* Approved/Authorized status: Execute, Print and Delete */}
                      {(wo.status === 'approved' || wo.status === 'authorized') && (
                        <>
                          {hasPermission('external_work_orders_execute') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleExecuteClick(wo.id)}
                              className="border-green-300 text-green-600 hover:bg-green-50"
                            >
                              Execute
                            </Button>
                          )}
                          {hasPermission('external_work_orders_view') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePrintWorkOrder(wo)}
                              className="border-blue-300 text-blue-600 hover:bg-blue-50"
                            >
                              Print
                            </Button>
                          )}
                          {canDelete('external_work_orders') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(wo.id)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          )}
                        </>
                      )}
                      
                      {/* Rejected/Denied/Executed status: Print and Delete */}
                      {(wo.status === 'rejected' || wo.status === 'denied' || wo.status === 'executed') && (
                        <>
                          {hasPermission('external_work_orders_view') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePrintWorkOrder(wo)}
                              className="border-blue-300 text-blue-600 hover:bg-blue-50"
                            >
                              Print
                            </Button>
                          )}
                          {canDelete('external_work_orders') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(wo.id)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {orders.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No work orders found</p>
            </div>
          )}
        </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                External Work Orders
              </h1>
              <p className="text-sm text-gray-600">Manage external work orders for sampling</p>
            </div>
          </div>
          {canCreate('external_work_orders') && (
            <Button 
              onClick={() => {
                setEditingWorkOrder(null);
                setFormData({ work_order_type: '', item_category: '', item_id: '', item_name: '', color_code: '', qty_meters: '', description: '', fund: '', requested_budget: '' });
                setOpen(true);
              }} 
              className="bg-black hover:bg-gray-800"
            >
              <Plus className="w-4 h-4 mr-2" /> New External Work Order
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Bar and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Master Search
              </label>
              <Input
                placeholder="Enter WO Number or Item Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Department
              </label>
              <SearchableSelect
                options={[
                  { value: '', label: 'All Departments' },
                  ...getUniqueItems()
                ]}
                value={itemFilter}
                onChange={(value) => setItemFilter(value)}
                placeholder="Select department..."
                searchPlaceholder="Search departments..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Fund
              </label>
              <SearchableSelect
                options={[
                  { value: '', label: 'All Funds' },
                  ...getUniqueFunds()
                ]}
                value={fundFilter}
                onChange={(value) => setFundFilter(value)}
                placeholder="Select fund..."
                searchPlaceholder="Search funds..."
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Filters apply across all status tabs and update totals dynamically
          </p>
        </div>

        {/* Status Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { key: 'pending', label: 'Pending', count: getWorkOrdersByStatus('pending').length },
                { key: 'approved', label: 'Approved/Authorized', count: getWorkOrdersByStatus('approved').length },
                { key: 'rejected', label: 'Rejected/Denied', count: getWorkOrdersByStatus('rejected').length },
                { key: 'executed', label: 'Executed', count: getWorkOrdersByStatus('executed').length }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.key
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {tab.label} <span className="ml-2 text-gray-500">{tab.count}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Work Orders Table */}
        {renderWorkOrdersTable(activeTab)}
      </main>

      {/* Create Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {editingWorkOrder ? 'Edit External Work Order' : 'Create External Work Order'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Work Order Type - First Field */}
            <div className="space-y-2">
              <Label>Work Order Type *</Label>
              <SearchableSelect
                options={workOrderTypeOptions}
                value={formData.work_order_type}
                onChange={handleWorkOrderTypeChange}
                placeholder="Select work order type..."
                searchPlaceholder="Search type..."
                required
              />
              <p className="text-xs text-gray-500">
                {formData.work_order_type === 'inventory_based' 
                  ? '✓ Barcode will be generated. Select item from inventory below.' 
                  : formData.work_order_type === 'non_inventory_based'
                  ? 'No inventory selection needed.'
                  : 'Select type to continue'}
              </p>
            </div>
            
            {/* Department Field - Shows for both types */}
            {formData.work_order_type && (
              <div className="space-y-2">
                <Label>Department *</Label>
                <SearchableSelect
                  options={[
                    { value: 'Executive', label: 'Executive' },
                    { value: 'Management', label: 'Management' },
                    { value: 'Merchandise', label: 'Merchandise' },
                    { value: 'Design Studio', label: 'Design Studio' },
                    { value: 'Sampling', label: 'Sampling' },
                    { value: 'Misc', label: 'Misc' },
                    ...getUniqueDepartments()
                  ]}
                  value={formData.department}
                  onChange={(value) => setFormData({ ...formData, department: value })}
                  placeholder="Select department..."
                  searchPlaceholder="Search departments..."
                  required
                />
              </div>
            )}

            {/* Inventory-Based Fields */}
            {formData.work_order_type === 'inventory_based' && (
              <>
                {/* Item Category Selection */}
                <div className="space-y-2">
                  <Label>Item Category *</Label>
                  <SearchableSelect
                    options={itemCategoryOptions}
                    value={formData.item_category}
                    onChange={handleItemCategoryChange}
                    placeholder="Select Fabric LN or Article LN-X..."
                    searchPlaceholder="Search category..."
                    required
                  />
                </div>

                {/* Item Selection */}
                {formData.item_category && (
                  <div className="space-y-2">
                    <Label>{formData.item_category === 'fabric' ? 'Select Fabric' : 'Select Article'} *</Label>
                    <SearchableSelect
                      options={getItemOptions()}
                      value={formData.item_id}
                      onChange={handleItemSelect}
                      placeholder={`Select ${formData.item_category}...`}
                      searchPlaceholder={`Search ${formData.item_category}...`}
                      required
                    />
                  </div>
                )}

                {/* Color Code Selection (for Fabrics only) */}
                {formData.item_category === 'fabric' && availableColors.length > 0 && (
                  <div className="space-y-2">
                    <Label>Color Code *</Label>
                    <SearchableSelect
                      options={getColorOptions()}
                      value={formData.color_code}
                      onChange={(value) => setFormData({ ...formData, color_code: value })}
                      placeholder="Select color code..."
                      searchPlaceholder="Search color..."
                      required
                    />
                  </div>
                )}

                {/* Quantity in Meters (for Fabrics only) */}
                {formData.item_category === 'fabric' && formData.color_code && (
                  <div className="space-y-2">
                    <Label>Quantity (Meters) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.qty_meters}
                      onChange={(e) => setFormData({ ...formData, qty_meters: e.target.value })}
                      placeholder="Enter quantity in meters"
                      required
                    />
                    <p className="text-xs text-gray-500">
                      This quantity will be used to issue fabric from inventory
                    </p>
                  </div>
                )}
              </>
            )}
            
            {/* Fund - Shows for both types */}
            {formData.work_order_type && (
              <div className="space-y-2">
                <Label>Fund *</Label>
                <SearchableSelect
                  options={fundOptions}
                  value={formData.fund}
                  onChange={(value) => setFormData({ ...formData, fund: value })}
                  placeholder="Select fund..."
                  searchPlaceholder="Search fund..."
                  required
                />
              </div>
            )}
            
            {/* Requested Budget - Shows for both types */}
            {formData.work_order_type && (
              <div className="space-y-2">
                <Label>Requested Budget (PKR)</Label>
                <Input
                  type="number"
                  placeholder="PKR"
                  value={formData.requested_budget}
                  onChange={(e) => setFormData({ ...formData, requested_budget: e.target.value })}
                  className="border-2 border-gray-300"
                  min="0"
                  step="0.01"
                />
              </div>
            )}
            
            {/* Description - Shows for both types */}
            {formData.work_order_type && (
              <div className="space-y-2">
                <Label>Description *</Label>
                <textarea
                  className="w-full px-3 py-2 border rounded-md min-h-[100px] focus:outline-none focus:ring-2 focus:ring-black"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the work order requirements..."
                  autoComplete="off"
                  required
                />
                <p className="text-xs text-gray-500">
                  Work order code will be auto-generated (WO-E-0001, WO-E-0002, etc.)
                </p>
              </div>
            )}
            
            <Button type="submit" className="w-full bg-black hover:bg-gray-800">
              {editingWorkOrder ? 'Update Work Order' : 'Create Work Order'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Work Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center border-b pb-3">
              <DialogTitle className="text-xl font-bold uppercase tracking-wide">
                WORK ORDER DETAILS
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html>
                      <head>
                        <title>Print Work Order - ${selectedWorkOrder?.wo_code}</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 20px; }
                          .header { border: 2px solid #000; padding: 15px; margin-bottom: 20px; }
                          .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin: 20px 0; }
                          .field { margin-bottom: 10px; }
                          .label { font-weight: bold; font-size: 12px; color: #666; }
                          .value { font-size: 14px; color: #000; }
                          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                          th, td { border: 1px solid #000; padding: 8px; text-align: left; }
                          th { background: #f0f0f0; }
                          @media print { body { margin: 0; } }
                        </style>
                      </head>
                      <body>
                        <div class="header">
                          <div class="grid">
                            <div>
                              <div class="label">WO NUMBER</div>
                              <div class="value" style="font-size: 20px; font-weight: bold;">${selectedWorkOrder?.wo_code || ''}</div>
                            </div>
                            <div style="text-align: right;">
                              ${selectedWorkOrder?.work_order_type === 'inventory_based' && selectedWorkOrder?.barcode && selectedWorkOrder?.barcode.image ? 
                                `<img src="${selectedWorkOrder.barcode.image}" alt="Barcode" style="height: 60px;"/>
                                <div style="font-size: 10px;">${selectedWorkOrder.wo_code}</div>` : 
                                '<div class="label">TYPE</div><div class="value">NON-INVENTORY</div>'}
                            </div>
                          </div>
                          <div class="grid" style="margin-top: 15px;">
                            <div>
                              <div class="label">${selectedWorkOrder?.item_category === 'fabric' ? 'FABRIC CODE' : selectedWorkOrder?.item_category === 'article' ? 'ARTICLE CODE' : 'ITEM'}</div>
                              <div class="value">${selectedWorkOrder?.item_name || 'N/A'}</div>
                            </div>
                            <div>
                              <div class="label">STATUS</div>
                              <div class="value">${selectedWorkOrder?.status?.toUpperCase() || ''}</div>
                            </div>
                          </div>
                          <div class="grid">
                            <div>
                              <div class="label">CREATED</div>
                              <div class="value">${new Date(selectedWorkOrder?.created_at).toLocaleString() || ''}</div>
                            </div>
                            <div>
                              <div class="label">CREATED BY</div>
                              <div class="value">${selectedWorkOrder?.created_by || 'N/A'}</div>
                            </div>
                          </div>
                        </div>
                        <div style="border: 1px solid #000; padding: 15px; margin-bottom: 20px;">
                          <div class="label">DESCRIPTION</div>
                          <div class="value">${selectedWorkOrder?.description || ''}</div>
                        </div>
                        ${selectedWorkOrder?.work_order_type === 'inventory_based' && selectedWorkOrder?.item_category === 'fabric' ? `
                          <div style="border: 1px solid #000; padding: 15px;">
                            <div class="label" style="margin-bottom: 10px;">FABRIC REQUIRED FOR INVENTORY ISSUANCE</div>
                            <table>
                              <thead>
                                <tr>
                                  <th>FABRIC CODE</th>
                                  <th>COLOR CODE</th>
                                  <th>QUANTITY</th>
                                  <th>FUND</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>${selectedWorkOrder?.item_name || 'N/A'}</td>
                                  <td>${selectedWorkOrder?.color_code || 'N/A'}</td>
                                  <td>${selectedWorkOrder?.qty_meters || 'N/A'} meters</td>
                                  <td>${selectedWorkOrder?.fund === 'petty_cash' ? 'Petty Cash' : 'Finance Department'}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        ` : ''}
                        ${selectedWorkOrder?.requested_budget ? `
                          <div style="border: 1px solid #000; padding: 15px; margin-bottom: 20px; background-color: #f9f9f9;">
                            <div style="color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px;">Requested Budget</div>
                            <div style="font-size: 20px; font-weight: bold;">PKR ${parseFloat(selectedWorkOrder.requested_budget).toLocaleString()}</div>
                          </div>
                        ` : ''}
                      </body>
                    </html>
                  `);
                  printWindow.document.close();
                  setTimeout(() => printWindow.print(), 250);
                }}
                className="flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print
              </Button>
            </div>
          </DialogHeader>
          {selectedWorkOrder && (
            <div className="py-4">
              {/* Header Box - Matching Internal WO Structure */}
              <div className="border-2 border-black p-6 mb-4">
                <div className="grid grid-cols-2 gap-6 mb-4">
                  {/* Left: WO Number */}
                  <div>
                    <Label className="text-gray-600 text-xs uppercase tracking-wider">WO NUMBER</Label>
                    <p className="text-3xl font-bold mt-1">{selectedWorkOrder.wo_code}</p>
                  </div>
                  
                  {/* Right: Barcode or Type */}
                  <div className="text-right">
                    {selectedWorkOrder.work_order_type === 'inventory_based' && selectedWorkOrder.barcode && selectedWorkOrder.barcode.image ? (
                      <div>
                        <img 
                          src={selectedWorkOrder.barcode.image} 
                          alt="Barcode" 
                          className="h-16 ml-auto mb-1"
                        />
                        <p className="text-xs text-gray-600">{selectedWorkOrder.wo_code}</p>
                      </div>
                    ) : (
                      <div>
                        <Label className="text-gray-600 text-xs uppercase tracking-wider">TYPE</Label>
                        <p className="text-lg font-semibold mt-1">NON-INVENTORY</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mt-6">
                  {/* Item Code */}
                  <div>
                    <Label className="text-gray-600 text-xs uppercase tracking-wider">
                      {selectedWorkOrder.item_category === 'fabric' ? 'FABRIC CODE' : 
                       selectedWorkOrder.item_category === 'article' ? 'ARTICLE CODE' : 'ITEM'}
                    </Label>
                    <p className="text-xl font-semibold mt-1">{selectedWorkOrder.item_name || 'N/A'}</p>
                  </div>
                  
                  {/* Status */}
                  <div>
                    <Label className="text-gray-600 text-xs uppercase tracking-wider">STATUS</Label>
                    <p className="text-xl font-semibold mt-1 uppercase">{selectedWorkOrder.status}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mt-4">
                  {/* Created Date */}
                  <div>
                    <Label className="text-gray-600 text-xs uppercase tracking-wider">CREATED</Label>
                    <p className="text-sm font-semibold mt-1">{new Date(selectedWorkOrder.created_at).toLocaleString()}</p>
                  </div>
                  
                  {/* Created By */}
                  <div>
                    <Label className="text-gray-600 text-xs uppercase tracking-wider">CREATED BY</Label>
                    <p className="text-sm font-semibold mt-1">{selectedWorkOrder.created_by || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Description Box */}
              <div className="border border-black p-4 mb-4">
                <Label className="text-gray-600 text-xs uppercase tracking-wider">DESCRIPTION</Label>
                <p className="text-sm mt-2 whitespace-pre-wrap">{selectedWorkOrder.description}</p>
              </div>
              
              {/* Item Details Table - For Inventory-Based Items */}
              {selectedWorkOrder.work_order_type === 'inventory_based' && (
                <div className="border border-black p-4 mb-4">
                  <Label className="text-gray-600 text-xs uppercase tracking-wider mb-3 block">
                    {selectedWorkOrder.item_category === 'fabric' 
                      ? 'FABRIC REQUIRED FOR INVENTORY ISSUANCE' 
                      : 'ARTICLE REQUIRED FOR INVENTORY ISSUANCE'}
                  </Label>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border border-gray-300 px-3 py-2 text-left text-xs uppercase tracking-wider">
                            {selectedWorkOrder.item_category === 'fabric' ? 'FABRIC CODE' : 'ARTICLE CODE'}
                          </th>
                          {selectedWorkOrder.item_category === 'fabric' && (
                            <>
                              <th className="border border-gray-300 px-3 py-2 text-left text-xs uppercase tracking-wider">COLOR CODE</th>
                              <th className="border border-gray-300 px-3 py-2 text-left text-xs uppercase tracking-wider">QUANTITY</th>
                            </>
                          )}
                          <th className="border border-gray-300 px-3 py-2 text-left text-xs uppercase tracking-wider">FUND</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 px-3 py-2 text-sm font-semibold">
                            {selectedWorkOrder.item_name || 'N/A'}
                          </td>
                          {selectedWorkOrder.item_category === 'fabric' && (
                            <>
                              <td className="border border-gray-300 px-3 py-2 text-sm">
                                {selectedWorkOrder.color_code || 'N/A'}
                              </td>
                              <td className="border border-gray-300 px-3 py-2 text-sm font-semibold">
                                {selectedWorkOrder.qty_meters || 'N/A'} meters
                              </td>
                            </>
                          )}
                          <td className="border border-gray-300 px-3 py-2 text-sm">
                            {fundOptions.find(f => f.value === selectedWorkOrder.fund)?.label || 'N/A'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Requested Budget - If exists */}
              {selectedWorkOrder.requested_budget && (
                <div className="bg-white p-4 rounded-lg border border-black mb-4">
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Requested Budget</Label>
                  <p className="text-xl font-bold mt-2">PKR {parseFloat(selectedWorkOrder.requested_budget).toLocaleString()}</p>
                </div>
              )}

              {/* Status Change Section - Only for Pending AND if user has approve permission */}
              {selectedWorkOrder.status === 'pending' && canApprove('external_work_orders') && (
                <div className="border border-blue-300 bg-blue-50 p-4 mb-4">
                  <Label className="text-sm font-bold text-gray-900 mb-3 block">Change Work Order Status</Label>
                  <SearchableSelect
                    options={getStatusOptionsForFund(selectedWorkOrder.fund)}
                    value=""
                    onChange={(value) => {
                      if (value) {
                        handleStatusChangeRequest(selectedWorkOrder.id, value);
                      }
                    }}
                    placeholder="Select new status..."
                    searchPlaceholder="Search status..."
                  />
                  <p className="text-xs text-gray-600 mt-2">
                    Authorization required to change status
                  </p>
                </div>
              )}
              
              {/* Financial Information - For Executed Work Orders */}
              {selectedWorkOrder.status === 'executed' && (selectedWorkOrder.total_value_pkr > 0 || selectedWorkOrder.total_value_usd > 0) && (
                <div className="border border-black p-4 mb-4">
                  <Label className="text-gray-600 text-xs uppercase tracking-wider mb-3 block">FINANCIAL SUMMARY</Label>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border border-gray-300 px-4 py-3 text-left text-xs uppercase tracking-wider">Description</th>
                          <th className="border border-gray-300 px-4 py-3 text-right text-xs uppercase tracking-wider">Amount (PKR)</th>
                          <th className="border border-gray-300 px-4 py-3 text-right text-xs uppercase tracking-wider">Amount (USD)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3 text-sm font-semibold">
                            Work Order Total Value
                          </td>
                          <td className="border border-gray-300 px-4 py-3 text-sm text-right font-semibold">
                            ₨ {parseFloat(selectedWorkOrder.total_value_pkr || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border border-gray-300 px-4 py-3 text-sm text-right font-semibold">
                            $ {parseFloat(selectedWorkOrder.total_value_usd || 0).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td className="border border-gray-300 px-4 py-3 text-sm font-bold uppercase">Grand Total:</td>
                          <td className="border border-gray-300 px-4 py-3 text-sm text-right font-bold">
                            ₨ {parseFloat(selectedWorkOrder.total_value_pkr || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border border-gray-300 px-4 py-3 text-sm text-right font-bold">
                            $ {parseFloat(selectedWorkOrder.total_value_usd || 0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Receipts Section - For Executed Work Orders */}
              {selectedWorkOrder.status === 'executed' && selectedWorkOrder.receipts && selectedWorkOrder.receipts.length > 0 && (
                <div className="bg-white p-5 rounded-lg border">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                      Payment Receipts ({selectedWorkOrder.receipts.length})
                    </Label>
                    <p className="text-xs text-gray-500">Click on any receipt to view and print</p>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {selectedWorkOrder.receipts.map((receipt, index) => (
                      <div 
                        key={index} 
                        className="relative group cursor-pointer"
                        onClick={() => handleReceiptClick(receipt)}
                      >
                        <img
                          src={receipt}
                          alt={`Receipt ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border-2 border-gray-300 hover:border-blue-500 transition-all duration-200 shadow-sm hover:shadow-md"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                          <span className="text-white opacity-0 group-hover:opacity-100 font-bold text-lg">
                            View
                          </span>
                        </div>
                        <div className="absolute top-2 right-2 bg-black/80 text-white text-xs font-bold px-2 py-1 rounded">
                          #{index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setViewDialogOpen(false)}
                >
                  Close
                </Button>
                <Button
                  variant="outline"
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Authorization Dialog for Delete */}
      <AuthorizationDialog
        open={authDialogOpen}
        onOpenChange={setAuthDialogOpen}
        onAuthorized={handleDeleteConfirm}
        title="Delete Work Order"
        description="Enter authorization code to delete this work order"
        correctCode="1436"
      />

      {/* Authorization Dialog for Status Change */}
      <AuthorizationDialog
        open={statusAuthDialogOpen}
        onOpenChange={setStatusAuthDialogOpen}
        onAuthorized={handleStatusChangeConfirm}
        title="Change Work Order Status"
        message={`Enter authorization code to change status to ${pendingStatusChange?.newStatus || ''}`}
        correctCode={selectedWorkOrder ? getAuthCodeForFund(selectedWorkOrder.fund) : 'extwo'}
      />

      {/* Execute Dialog - Upload Receipts and Enter Total Value */}
      <Dialog open={executeDialogOpen} onOpenChange={setExecuteDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Execute Work Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Upload Receipts *</Label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={bypassReceipts}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">You can upload multiple images</p>
            </div>

            {receiptImages.length > 0 && (
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  Uploaded Receipts ({receiptImages.length})
                </Label>
                <div className="grid grid-cols-4 gap-3">
                  {receiptImages.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img}
                        alt={`Receipt ${index + 1}`}
                        className="w-full h-24 object-cover rounded border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Total Value (PKR) *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">PKR</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={totalValuePKR}
                  onChange={(e) => setTotalValuePKR(e.target.value)}
                  placeholder="0.00"
                  className="pl-14"
                  required={!bypassReceipts}
                  disabled={bypassReceipts}
                />
              </div>
              {totalValuePKR && parseFloat(totalValuePKR) > 0 && (
                <div className="mt-2 p-3 bg-blue-50 rounded-md border border-blue-200">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Converted Value:</span> USD ${convertPKRtoUSD(totalValuePKR)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Exchange Rate: 1 USD = {PKR_TO_USD_RATE} PKR
                  </p>
                </div>
              )}
            </div>

            {/* Bypass Option */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="bypassReceipts"
                  checked={bypassReceipts}
                  onChange={(e) => setBypassReceipts(e.target.checked)}
                  className="mt-1 w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                />
                <div className="flex-1">
                  <label 
                    htmlFor="bypassReceipts" 
                    className="text-sm font-medium text-gray-900 cursor-pointer"
                  >
                    Bypass receipts and total value requirement
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Check this option if receipts are not available or payment information is not required for this work order
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setExecuteDialogOpen(false);
                  setReceiptImages([]);
                  setTotalValuePKR('');
                  setBypassReceipts(false);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleExecuteSubmit} className="bg-green-600 hover:bg-green-700">
                Execute & Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Viewer Dialog */}
      <Dialog open={receiptViewOpen} onOpenChange={setReceiptViewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Receipt Image
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrintReceipt}
                className="flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print Receipt
              </Button>
            </div>
          </DialogHeader>
          {selectedReceipt && (
            <div className="py-4">
              <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center">
                <img
                  src={selectedReceipt}
                  alt="Receipt"
                  className="max-w-full max-h-[70vh] object-contain rounded shadow-lg"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
